import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Formik, Form } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { rutValidationSchema } from '../preloan/v3/validationSchemas';
import { setRut } from '../../utils/helpers';
import { postDataDeletionRequest } from '../../queries/data-deletion';
import analytics from '../../utils/analytics';

type Status = 'idle' | 'submitted' | 'activeLoan' | 'loading' | 'error';

const ACTIVE_LOAN_IDENTIFIERS = ['activeloanerror', 'active loan', 'crédito activo'];

function isActiveLoanError(e: unknown): boolean {
  if (e === 400) return true;
  if (typeof e === 'string') {
    return ACTIVE_LOAN_IDENTIFIERS.some((id) => e.toLowerCase().includes(id));
  }
  return false;
}

export default function RequestDeletionForm() {
  const [status, setStatus] = React.useState<Status>('idle');
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [recaptchaCheckError, setRecaptchaCheckError] = React.useState(false);

  const recaptchaOnChange = () => {
    analytics.track('Click Checkbox', {
      texto: 'No soy un robot',
      initiator: 'usuario',
    });
  };
  const theme = useTheme();
  if (status === 'submitted') {
    return (
      <Box textAlign="center">
        <Typography variant="h6" gutterBottom>
          ¡Solicitud recibida!
        </Typography>
        <Alert sx={{ textAlign: 'left', mt: 5, backgroundColor: theme.palette.grey[100] }}>
          Si encontramos una cuenta asociada a ese RUT,
          recibirás un link a tu email para confirmar la eliminación.
        </Alert>
      </Box>
    );
  }

  if (status === 'activeLoan') {
    return (
      <Box textAlign="center">
        <Alert severity="error" sx={{ textAlign: 'left', mt: 2 }}>
          No puedes eliminar tus datos mientras tengas un crédito activo.
        </Alert>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Typography variant="body1" gutterBottom sx={{ marginTop: 5 }}>
        Ha ocurrido un error, por favor contáctanos.
      </Typography>
    );
  }
  return (
    <Formik
      initialValues={{ rut: '' }}
      validationSchema={rutValidationSchema}
      onSubmit={async (values) => {
        setStatus('loading');
        setRecaptchaCheckError(false);
        let recaptchaValue;
        if (recaptchaRef.current) {
          recaptchaValue = recaptchaRef.current.getValue();
          if (!recaptchaValue) {
            setRecaptchaCheckError(true);
            setStatus('idle');
            return;
          }
        }
        try {
          await postDataDeletionRequest(values.rut, recaptchaValue);
          setStatus('submitted');
        } catch (e) {
          if (isActiveLoanError(e)) {
            setStatus('activeLoan');
          } else {
            setStatus('error');
          }
        }
      }}
    >
      {({
        values, handleBlur, touched, errors, setFieldValue, isSubmitting,
      }) => (
        <Form style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body1" gutterBottom>
            Ingresa tu RUT para solicitar la eliminación de tus datos.
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            autoComplete="off"
            placeholder="12.345.678-9"
            id="rut"
            name="rut"
            autoFocus
            error={touched.rut && Boolean(errors.rut)}
            helperText={(touched.rut && errors.rut) || ' '}
            FormHelperTextProps={{ style: { textAlign: 'center', height: 10 } }}
            inputProps={{ style: { textAlign: 'center' } }}
            value={values.rut}
            onChange={(e) => setRut(setFieldValue, e.target.value)}
            onBlur={(e) => {
              setRut(setFieldValue, values.rut);
              handleBlur(e);
            }}
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 2.5 }}>
            <ReCAPTCHA
              onChange={recaptchaOnChange}
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_CAPTCHA_PLRV2 as string}
            />
          </Box>
          {recaptchaCheckError && <Typography color="error" variant="caption">Queremos saber si no eres un robot </Typography>}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting || status === 'loading' || !touched.rut}
            sx={{
              mt: 2.5,
              width: 'auto',
              minWidth: 220,
              paddingInline: 2.5,
              textTransform: 'none',
            }}
          >
            {status === 'loading'
              ? <CircularProgress size={22} color="inherit" />
              : 'Solicitar eliminación de mis datos'}
          </Button>
        </Form>
      )}
    </Formik>
  );
}
